
import Header from "@/components/Header";
import { useMarketplace } from "@/hooks/useMarketplace";
import { useAccountPerformance } from "@/hooks/marketplace/useAccountPerformance";
import MarketplaceHeader from "@/components/marketplace/MarketplaceHeader";
import MarketplaceContent from "@/components/marketplace/content/MarketplaceContent";
import StrategyDetailContainer from "@/components/marketplace/details/StrategyDetailContainer";
import Footer from "@/components/Footer";
import ProtectedRoute from "@/components/auth/ProtectedRoute";
import { useEffect, useState } from "react";
import MetaTags from "@/components/meta/MetaTags";
import { useIsMobile } from "@/hooks/use-mobile";
import { useAuth } from "@/contexts/AuthContext";
import { processReferral } from "@/services/referralService";
import { isInitDone, setIsInitDone, subscribeToKlaviyo } from "@/utils/authUtils";
import { createCampaignTrial } from "@/utils/campaignUtils";
import ChatOption from "@/components/ChatOption";
import { getCampaignCookie } from "@/hooks/referrals/referralUtils";

const Marketplace = () => {
  const [isInitialized, setIsInitialized] = useState(false);
  const isMobile = useIsMobile();
  const { user, isAuthenticated } = useAuth();

  // Process user-specific actions once on component mount
  useEffect(() => {
    const processUserActions = async () => {
      let done = await isInitDone(user.id);
      console.log("INIT DONE: ", done);
      if (user && !done) {
        try {
          const campaign = getCampaignCookie();
          // Run these actions in parallel
          const results = await Promise.allSettled([
            processReferral(user.id),
            createCampaignTrial(user.id),
            subscribeToKlaviyo(
              user.id,
              user.email,
              user.user_metadata?.first_name ?? "",
              user.user_metadata?.last_name ?? ""
            )
          ]);

          console.log("SET INIT DONE");
          await setIsInitDone(user.id, campaign);
          done = await isInitDone(user.id);
          console.log("INIT DONE: ", done);

          results.forEach((result, index) => {
            const taskNames = ['Process Referral', 'Create Campaign Trial', 'Klaviyo Subscription'];
            if (result.status === 'fulfilled') {
              console.log(`Task "${taskNames[index]}" completed successfully:`, result.value);

            } else {
              console.error(`Task "${taskNames[index]}" failed:`, result.reason);
            }
          });
        } catch (error) {
          console.error("Error processing user actions:", error);
        }
      }
      
      setIsInitialized(true);
    };

    if (isAuthenticated) {
      processUserActions();
    } else {
      setIsInitialized(true);
    }
  }, [user, isAuthenticated]);

  const {
    strategies,
    loading,
    showMoreFilters,
    setShowMoreFilters,
    topPerformers,
    setTopPerformers,
    selectedStrategy,
    setSelectedStrategy,
    filters,
    setFilters,
    sortBy,
    setSortBy,
    activeFiltersCount,
  } = useMarketplace();

  // Use our custom hook to get account performance data
  const { accountPerformance, performanceLoading } = useAccountPerformance(strategies, loading);

  // If initialization isn't completed, show nothing (ProtectedRoute will handle loading state)
  if (!isInitialized) return null;

  return (
    <ProtectedRoute>
      <div className={`min-h-screen flex flex-col bg-gradient-to-b from-white to-gray-50 ${isMobile ? 'pb-14' : ''}`}>
        {/* Add MetaTags specifically for the marketplace page */}
        <MetaTags
          customTitle="Marketplace - Metrade"
          customDescription="Explore and invest in verified crypto trading strategies"
          customUrl="https://metrade.io/marketplace"
          customImage="https://metrade.io/lovable-uploads/bf9cdc94-db08-4840-bae6-869c2f0c41bb.png"
        />

        <Header />
        <main className="flex-1 container mx-auto px-4">
          <MarketplaceHeader
            activeFiltersCount={activeFiltersCount}
            sortBy={sortBy}
            onSortChange={setSortBy}
          />

          <MarketplaceContent
            strategies={strategies}
            loading={loading}
            performanceLoading={performanceLoading}
            accountPerformance={accountPerformance}
            showMoreFilters={showMoreFilters}
            setShowMoreFilters={setShowMoreFilters}
            topPerformers={topPerformers}
            setTopPerformers={setTopPerformers}
            selectedStrategy={selectedStrategy}
            setSelectedStrategy={setSelectedStrategy}
            filters={filters}
            setFilters={setFilters}
            sortBy={sortBy}
            setSortBy={setSortBy}
          />
        </main>

        <StrategyDetailContainer
          selectedStrategy={selectedStrategy}
          setSelectedStrategy={setSelectedStrategy}
          strategies={strategies}
          accountPerformance={accountPerformance}
        />
        {/* Only show ChatOption on desktop */}
        {!isMobile && (
          <ChatOption />
        )}

        <Footer />
      </div>
    </ProtectedRoute>
  );
};

export default Marketplace;
